/**
 * Product Carousel Widget
 *
 * Self-initializing product carousel embedded inside blog post content:
 *   <div class="product-carousel" data-collection="abstract-rugs" data-limit="4" data-columns="4"></div>
 * Supports configurable column counts via `data-columns` and dynamic arrow visibility.
 *
 * @version 1.1.0
 */
(function () {
  'use strict';

  var DEFAULT_LIMIT = 4;
  var DEFAULT_COLUMNS_DESKTOP = 4;
  var DEFAULT_COLUMNS_MOBILE = 1.2;
  var INITIALIZED_ATTR = 'data-carousel-initialized';

  /**
   * Formats a product price as a localized currency string.
   * @param {string|number} price - Decimal price string or number.
   * @returns {string} Formatted currency string.
   */
  function formatPrice(price) {
    // Parse input into float.
    var amount = parseFloat(price);
    // If not a valid number, return empty string.
    if (isNaN(amount)) return '';

    // Default currency code for Sydney Rugs Online.
    var currencyCode = 'AUD';
    // Read active storefront currency if present.
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      currencyCode = window.Shopify.currency.active;
    }

    try {
      // Format price using Intl NumberFormat.
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch (error) {
      // Fallback formatting if Intl fails.
      return '$' + amount.toFixed(2);
    }
  }

  /**
   * Builds the DOM structure for a single carousel slide.
   * @param {Object} product - Product object from products.json.
   * @returns {HTMLElement} Slide element.
   */
  function buildSlide(product) {
    // Create outer slide wrapper and assign class.
    var slide = document.createElement('div');
    slide.className = 'product-carousel__slide';

    // Create product link element and point to PDP.
    var link = document.createElement('a');
    link.className = 'product-carousel__link';
    link.href = '/products/' + product.handle;

    // Create product image element and set attributes.
    var image = document.createElement('img');
    var firstImage = product.images && product.images[0];
    image.className = 'product-carousel__image';
    image.src = firstImage ? firstImage.src : '';
    image.alt = (firstImage && firstImage.alt) || product.title || '';
    image.loading = 'lazy';
    image.width = 300;
    image.height = 300;

    // Create product title element and set content.
    var title = document.createElement('p');
    title.className = 'product-carousel__title';
    title.textContent = product.title;

    // Append image and title to product card link.
    link.appendChild(image);
    link.appendChild(title);

    // Extract first variant for price display.
    var firstVariant = product.variants && product.variants[0];
    if (firstVariant) {
      var price = document.createElement('p');
      price.className = 'product-carousel__price';
      price.textContent = formatPrice(firstVariant.price);
      link.appendChild(price);
    }

    // Append link inside slide and return slide element.
    slide.appendChild(link);
    return slide;
  }

  /**
   * Scrolls the track by roughly one visible page of items.
   * @param {HTMLElement} track - Scrollable track element.
   * @param {number} direction - 1 for next, -1 for prev.
   */
  function scrollByPage(track, direction) {
    // Perform smooth page scroll.
    track.scrollBy({ left: track.clientWidth * direction, behavior: 'smooth' });
  }

  /**
   * Renders the fetched products and manages navigation buttons.
   * @param {HTMLElement} container - Carousel placeholder container.
   * @param {Array<Object>} products - Array of product objects.
   */
  function renderCarousel(container, products) {
    // If no products available, hide container and exit.
    if (!products.length) {
      container.hidden = true;
      return;
    }

    // Create horizontal scroll track.
    var track = document.createElement('div');
    track.className = 'product-carousel__track';

    // Build and append each slide to the track.
    products.forEach(function (product) {
      track.appendChild(buildSlide(product));
    });

    // Create previous navigation button.
    var prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'product-carousel__nav product-carousel__nav--prev is-hidden';
    prevButton.setAttribute('aria-label', 'Previous products');
    prevButton.hidden = true;
    prevButton.addEventListener('click', function () { scrollByPage(track, -1); });

    // Create next navigation button.
    var nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'product-carousel__nav product-carousel__nav--next is-hidden';
    nextButton.setAttribute('aria-label', 'Next products');
    nextButton.hidden = true;
    nextButton.addEventListener('click', function () { scrollByPage(track, 1); });

    // Append track and navigation buttons to container.
    container.appendChild(track);
    container.appendChild(prevButton);
    container.appendChild(nextButton);

    /**
     * Evaluates track scroll metrics and toggles arrow visibility.
     * When all items fit or reach boundaries, unnecessary arrows are hidden.
     */
    function updateNavButtons() {
      // Sub-pixel tolerance threshold in pixels.
      var tolerance = 2;
      // Calculate whether items exceed visible track width.
      var hasOverflow = track.scrollWidth > track.clientWidth + tolerance;

      // When all items fit without scrolling, hide both arrows.
      if (!hasOverflow) {
        prevButton.classList.add('is-hidden');
        prevButton.hidden = true;
        nextButton.classList.add('is-hidden');
        nextButton.hidden = true;
        return;
      }

      // Hide previous button if at start of track, otherwise show it.
      var atStart = track.scrollLeft <= tolerance;
      prevButton.classList.toggle('is-hidden', atStart);
      prevButton.hidden = atStart;

      // Hide next button if scrolled to end of track, otherwise show it.
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - tolerance;
      nextButton.classList.toggle('is-hidden', atEnd);
      nextButton.hidden = atEnd;
    }

    // Flag to avoid duplicate animation frames during scroll.
    var isTicking = false;
    // Update button states on track scroll.
    track.addEventListener('scroll', function () {
      if (!isTicking) {
        window.requestAnimationFrame(function () {
          updateNavButtons();
          isTicking = false;
        });
        isTicking = true;
      }
    }, { passive: true });

    // Observe track resizing to re-evaluate overflow when container width changes.
    if (typeof ResizeObserver !== 'undefined') {
      var observer = new ResizeObserver(function () { updateNavButtons(); });
      observer.observe(track);
    } else {
      window.addEventListener('resize', updateNavButtons, { passive: true });
    }

    // Schedule initial button state evaluation.
    window.requestAnimationFrame(function () { updateNavButtons(); });
  }

  /**
   * Reads settings, fetches collection products, and triggers render.
   * @param {HTMLElement} container - Carousel placeholder container.
   */
  function loadCarousel(container) {
    // Read collection handle from data attribute.
    var handle = container.getAttribute('data-collection');
    if (!handle) {
      console.warn('Product carousel: missing required "data-collection" attribute.', container);
      return;
    }

    // Read limit attribute, fallback to default.
    var limit = parseInt(container.getAttribute('data-limit'), 10);
    if (isNaN(limit) || limit <= 0) limit = DEFAULT_LIMIT;

    // Read custom desktop columns attribute (e.g. data-columns="3" or data-columns="4").
    var colsDesktop = parseInt(container.getAttribute('data-columns') || container.getAttribute('data-columns-desktop'), 10);
    if (!isNaN(colsDesktop) && colsDesktop > 0) {
      container.style.setProperty('--carousel-cols-desktop', colsDesktop);
    } else {
      container.style.setProperty('--carousel-cols-desktop', DEFAULT_COLUMNS_DESKTOP);
    }

    // Read custom mobile columns attribute (e.g. data-columns-mobile="1.2" or "2").
    var colsMobile = parseFloat(container.getAttribute('data-columns-mobile'));
    if (!isNaN(colsMobile) && colsMobile > 0) {
      container.style.setProperty('--carousel-cols-mobile', colsMobile);
    } else {
      container.style.setProperty('--carousel-cols-mobile', DEFAULT_COLUMNS_MOBILE);
    }

    // Add loading indicator class.
    container.classList.add('product-carousel--loading');

    // Fetch collection products from Shopify storefront JSON endpoint.
    fetch('/collections/' + handle + '/products.json?limit=' + limit)
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed with status ' + response.status);
        return response.json();
      })
      .then(function (data) {
        renderCarousel(container, (data && data.products) || []);
      })
      .catch(function (error) {
        console.warn('Product carousel: unable to load collection "' + handle + '".', error);
        container.hidden = true;
      })
      .finally(function () {
        container.classList.remove('product-carousel--loading');
      });
  }

  /**
   * Discovers and initializes all uninitialized product carousels.
   * Utilizes IntersectionObserver for lazy loading off-screen carousels.
   */
  function initCarousels() {
    // Query all uninitialized carousel containers.
    var containers = document.querySelectorAll('.product-carousel:not([' + INITIALIZED_ATTR + '])');

    // Iterate through each container found.
    containers.forEach(function (container) {
      // Mark container as initialized.
      container.setAttribute(INITIALIZED_ATTR, 'true');

      // Use IntersectionObserver when supported.
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries, currentObserver) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                loadCarousel(entry.target);
                currentObserver.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '200px 0px' }
        );
        observer.observe(container);
      } else {
        // Direct fallback loading when IntersectionObserver unavailable.
        loadCarousel(container);
      }
    });
  }

  // Bootstrap initialization when DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }
})();
